import {
  useDeleteStopList,
  useStopList,
  useStopListSettings,
  useUpdateStopList,
  useUpdateStopListSettings,
} from "@/api/stop-list.api";
import { Button } from "@/components/generic/Button";
import { CheckCard } from "@/components/generic/CheckCard";
import { Input } from "@/components/generic/Field";
import { Modal } from "@/components/generic/Modal";
import { PageHeader } from "@/components/layout/PageHeader";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { DialogTrigger, Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const stopListSearchSchema = z.object({
  name: z.string().optional(),
});

type StopListSearch = z.infer<typeof stopListSearchSchema>;

const stopListFormSchema = z.object({
  name: z.string().min(1, "Navn er påkrævet"),
  isVisible: z.boolean(),
  isAlwaysEnabled: z.boolean(),
});

type StopListFormData = z.infer<typeof stopListFormSchema>;

export const Route = createFileRoute(
  "/_dashboard/settings/_admin/stop-lists/$id/",
)({
  validateSearch: (search): StopListSearch =>
    stopListSearchSchema.parse(search),
  component: RouteComponent,
  staticData: {
    isBreadcrumbRoot: true,
  },
  loader: async () => ({
    crumb: "Stopliste",
  }),
});

function RouteComponent() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const isVirtualList = id === "virtual-all-leads";
  const stoplist = useStopList(id);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const deleteStopListMutation = useDeleteStopList();
  const updateStopList = useUpdateStopList();
  const setting = useStopListSettings();
  const settingUpdate = useUpdateStopListSettings();

  const stopListName = search.name || "STOPLISTE NAVN";

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
  } = useForm<StopListFormData>({
    resolver: zodResolver(stopListFormSchema),
    defaultValues: {
      name: stopListName,
      isVisible: false,
      isAlwaysEnabled: false,
    },
  });

  useEffect(() => {
    if (!isVirtualList && stoplist?.data) {
      reset({
        name: stopListName,
        isVisible: !stoplist.data.hidden,
        isAlwaysEnabled: stoplist.data.mandatory ?? false,
      });
    } else if (isVirtualList && setting.data) {
      reset({
        name: stopListName,
        isVisible: false,
        isAlwaysEnabled: setting.data.excludeAlreadyDelivered ?? false,
      });
    }
  }, [stoplist?.data, setting.data, isVirtualList, reset, stopListName]);

  const onSubmit = async (data: StopListFormData) => {
    if (!isVirtualList) {
      await updateStopList.mutateAsync({
        id: id,
        data: {
          Name: data.name,
          Mandatory: data.isAlwaysEnabled,
          Hidden: !data.isVisible,
        },
      });
    } else {
      await settingUpdate.mutateAsync({
        excludeAlreadyDelivered: data.isAlwaysEnabled,
      });
    }
    navigate({ href: "/settings/stop-lists" });
  };

  const handleDelete = () => {
    if (id) {
      deleteStopListMutation.mutate(id);
      setDeleteOpen(false);
      navigate({ href: "/settings/stop-lists" });
    }
  };

  return (
    <>
      <PageHeader
        backLinkOptions={{
          from: Route.fullPath,
          to: "/settings/stop-lists",
        }}
        breadcrumbs={{
          className: "uppercase",
          from: Route.fullPath,
          fromCrumb: search.name || stopListName,
        }}
        sticky={false}
      />

      <SettingsContainer
        backLinkOptions={{ to: "/settings/stop-lists" }}
        className="mx-14 mt-6 mb-6 !p-0"
      >
        <Form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col items-center"
        >
          <div className="mb-10 flex max-w-md flex-col items-center gap-6">
            <h1 className="text-md font-medium">Indstillinger for stopliste</h1>
            <span className="text-theme-on-background-variant text-center text-sm">
              {isVirtualList
                ? "Denne stopliste er automatisk oprettet, og indeholder alle de leads, som du har modtaget fra alle kampagner. Du kan vælge at aktivere den for alle kampagner, eller at den skal aktiveres efter behov."
                : `Denne stopliste er manuelt oprettet, og indeholder ${
                    stoplist.data?.count
                  } firmaer. Den ${
                    stoplist.data?.mandatory
                      ? "er automatisk aktiv for alle kampagner"
                      : stoplist.data?.campaignCount
                        ? "er aktiveret for " +
                          stoplist.data?.campaignCount +
                          " kampagner"
                        : "bruges ikke i øjeblikket ikke i nogen kampagner"
                  }.`}
            </span>
          </div>

          <div className="w-full max-w-sm space-y-4">
            {!isVirtualList && (
              <>
                <div>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="name"
                        type="text"
                        className="border-theme-outline-variant h-20"
                        aria-invalid={!!errors.name}
                        aria-describedby={
                          errors.name ? "name-error" : undefined
                        }
                        placeholder="Stoplisten navn"
                      />
                    )}
                  />
                  {errors.name && (
                    <p id="name-error" className="mt-2 text-sm text-red-500">
                      {errors.name.message}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="h-20">
              <CheckCard
                control={control}
                name="isAlwaysEnabled"
                label="Aktiv for alle kampagner"
              />
            </div>

            {!isVirtualList && (
              <div className="h-20">
                <CheckCard
                  control={control}
                  name="isVisible"
                  label="Synlig under kampagneopsætning"
                />
              </div>
            )}

            <div className="flex justify-center gap-4 pt-10">
              {!isVirtualList && (
                <DialogTrigger>
                  <Button
                    type="button"
                    intent="secondary"
                    size="lg"
                    onPress={() => setDeleteOpen(true)}
                    isDisabled={isSubmitting}
                  >
                    Slet stopliste
                  </Button>
                  <Modal
                    isOpen={isDeleteOpen}
                    onOpenChange={setDeleteOpen}
                    size="lg"
                    intent="default"
                    icon={
                      <Trash
                        size={30}
                        className="text-theme-gradient-from/90"
                      />
                    }
                    animation="scale"
                    className="flex justify-center"
                  >
                    <div className="flex max-w-104.75 flex-col items-center gap-4">
                      <span className="text-md text-theme-on-glass font-medium">
                        Slet stopliste
                      </span>
                      <span className="text-theme-on-glass-variant text-center text-sm font-medium">
                        {stoplist.data?.mandatory
                          ? "Denne stopliste er aktiveret for alle aktive kampagner. Hvis du sletter den, vil det kunne påvirke udfaldet af disse kampagner."
                          : stoplist.data?.campaignCount !== 0
                            ? `Denne stopliste bruges af ${stoplist.data?.campaignCount} kampagner. Hvis du sletter den, vil det kunne påvirke udfaldet af disse kampagner.`
                            : "Denne stopliste er ikke i brug, og du kan derfor slette den uden at påvirke aktive kampagner."}
                      </span>
                      {(stoplist.data?.campaignCount ||
                        stoplist.data?.mandatory) && (
                        <span className="text-theme-on-glass-variant text-center text-sm">
                          Du kan i stedet vælge at skjule stoplisten for
                          fremtidige kampagner.
                        </span>
                      )}
                    </div>

                    <div className="flex justify-center gap-6 pt-10">
                      <Button animate slot="close" intent="secondary">
                        Fortryd
                      </Button>
                      <Button animate onClick={() => handleDelete()}>
                        Bekræft og slet
                      </Button>
                    </div>
                  </Modal>
                </DialogTrigger>
              )}
              <Button
                type="submit"
                size="lg"
                animate
                isDisabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? "Gemmer..." : "Gem"}
              </Button>
            </div>
          </div>
        </Form>
      </SettingsContainer>
    </>
  );
}
